import React from 'react';

interface MockClientProgressProps {
    variant?: 'full' | 'compact';
    className?: string;
}

const milestones = [
    { label: 'Initial', sublabel: 'Assessment', completed: true },
    { label: '4-Week', sublabel: 'Check-in', completed: true },
    { label: '8-Week', sublabel: 'Review', completed: true },
    { label: 'Current', sublabel: 'Status', completed: false },
];

const metrics = [
    { label: 'Mobility', value: 82, delta: '+24%', color: '#22C55E' },
    { label: 'Strength', value: 68, delta: '+31%', color: '#3B82F6' },
    { label: 'Balance', value: 91, delta: '+18%', color: '#EAB308' },
];

const MockClientProgress: React.FC<MockClientProgressProps> = ({ variant = 'full', className = '' }) => {
    const compact = variant === 'compact';
    const viewW = compact ? 360 : 440;
    const viewH = compact ? 200 : 250;
    const fontSize = compact ? 8 : 10;
    const smallFont = compact ? 7 : 8;

    // Timeline section
    const timelineY = compact ? 30 : 38;
    const nodeR = compact ? 5 : 6;
    const timelineStartX = 40;
    const timelineEndX = viewW - 40;
    const timelineSpan = timelineEndX - timelineStartX;
    const nodeSpacing = timelineSpan / (milestones.length - 1);

    // Progress bars section
    const barsStartY = compact ? 80 : 100;
    const barH = compact ? 8 : 10;
    const barW = compact ? 160 : 200;
    const barX = compact ? 70 : 90;
    const barSpacing = compact ? 30 : 38;

    return (
        <div className={`w-full ${className}`}>
            <svg viewBox={`0 0 ${viewW} ${viewH}`} className="w-full h-auto" fill="none">
                {/* Title */}
                <text x={12} y={16} fill="white" fontSize={compact ? 9 : 11} fontWeight="600" fontFamily="Inter, sans-serif">
                    CLIENT PROGRESS
                </text>

                {/* Timeline */}
                {milestones.map((ms, i) => {
                    const x = timelineStartX + i * nodeSpacing;
                    const isLast = i === milestones.length - 1;

                    return (
                        <g key={ms.label}>
                            {/* Connecting line to next node */}
                            {i < milestones.length - 1 && (
                                <line
                                    x1={x + nodeR}
                                    y1={timelineY}
                                    x2={timelineStartX + (i + 1) * nodeSpacing - nodeR}
                                    y2={timelineY}
                                    stroke={milestones[i + 1].completed ? 'white' : 'rgba(255,255,255,0.2)'}
                                    strokeWidth="1"
                                    strokeDasharray={milestones[i + 1].completed ? 'none' : '4 3'}
                                    className={!milestones[i + 1].completed ? 'animate-dash-flow' : undefined}
                                />
                            )}

                            {/* Node */}
                            <circle
                                cx={x}
                                cy={timelineY}
                                r={nodeR}
                                fill={ms.completed ? 'white' : 'transparent'}
                                stroke="white"
                                strokeWidth={isLast ? 1.5 : 1}
                                className={isLast ? 'animate-data-pulse' : undefined}
                            />
                            {ms.completed && (
                                <polyline
                                    points={`${x - 2.5},${timelineY} ${x - 0.5},${timelineY + 2} ${x + 2.5},${timelineY - 2}`}
                                    stroke="black"
                                    strokeWidth="1.5"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            )}

                            {/* Labels */}
                            <text x={x} y={timelineY + nodeR + 12} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize={fontSize} fontWeight="500" fontFamily="Inter, sans-serif">
                                {ms.label}
                            </text>
                            <text x={x} y={timelineY + nodeR + 22} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize={smallFont} fontFamily="Inter, sans-serif">
                                {ms.sublabel}
                            </text>
                        </g>
                    );
                })}

                {/* Separator */}
                <line x1={12} y1={barsStartY - 10} x2={viewW - 12} y2={barsStartY - 10} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />

                {/* Progress bars */}
                {metrics.map((metric, i) => {
                    const y = barsStartY + i * barSpacing;
                    const fillW = (metric.value / 100) * barW;

                    return (
                        <g key={metric.label}>
                            {/* Label */}
                            <text x={12} y={y + barH / 2 + fontSize / 2 - 1} fill="rgba(255,255,255,0.6)" fontSize={fontSize} fontFamily="Inter, sans-serif">
                                {metric.label}
                            </text>

                            {/* Bar background */}
                            <rect x={barX} y={y} width={barW} height={barH} fill="rgba(255,255,255,0.06)" />
                            {/* Bar fill */}
                            <rect x={barX} y={y} width={fillW} height={barH} fill={metric.color} opacity="0.7" />

                            {/* Percentage */}
                            <text x={barX + barW + 8} y={y + barH / 2 + fontSize / 2 - 1} fill={metric.color} fontSize={fontSize} fontWeight="600" fontFamily="Inter, sans-serif">
                                {metric.value}%
                            </text>

                            {/* Delta */}
                            <text x={barX + barW + 40} y={y + barH / 2 + fontSize / 2 - 1} fill="rgba(255,255,255,0.35)" fontSize={smallFont} fontFamily="Inter, sans-serif">
                                {metric.delta}
                            </text>
                        </g>
                    );
                })}

                {/* Overall progress */}
                {(() => {
                    const overallY = barsStartY + metrics.length * barSpacing + 4;
                    const overallAvg = Math.round(metrics.reduce((s, m) => s + m.value, 0) / metrics.length);
                    return (
                        <g>
                            <line x1={12} y1={overallY - 6} x2={viewW - 12} y2={overallY - 6} stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
                            <text x={12} y={overallY + fontSize} fill="rgba(255,255,255,0.4)" fontSize={fontSize} fontFamily="Inter, sans-serif">
                                Overall Progress
                            </text>
                            <text x={barX + barW + 8} y={overallY + fontSize} fill="white" fontSize={fontSize} fontWeight="600" fontFamily="Inter, sans-serif">
                                {overallAvg}%
                            </text>
                        </g>
                    );
                })()}
            </svg>
        </div>
    );
};

export default MockClientProgress;
